import React, { useState, useEffect, useCallback } from 'react';
import { api, API_ROUTERS } from '../../../services/api';
import CadastrosEmpresa from './view';
import {
  Empresa,
  FormDataEmpresa,
  ChaveAplicacao,
  ChaveGerada,
} from './types';
import Store, { useAppDispatch } from '../../../store';

const initialFormData: FormDataEmpresa = {
  identificador: '',
  nome: '',
  banco_erp: '',
  url_erp: '',
  token_erp: '',
  ativo: true,
};

const CadastrosEmpresaContainer: React.FC = () => {
  const dispatch = useAppDispatch();

  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [error, setError] = useState<string>('');
  const [formData, setFormData] = useState<FormDataEmpresa>(initialFormData);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [isChavesModalOpen, setIsChavesModalOpen] = useState<boolean>(false);
  const [empresaSelecionada, setEmpresaSelecionada] = useState<Empresa | null>(
    null
  );
  const [chaves, setChaves] = useState<ChaveAplicacao[]>([]);
  const [chaveGerada, setChaveGerada] = useState<ChaveGerada | null>(null);
  const [propagar, setPropagar] = useState<boolean>(true);
  const [chaveParaRevogar, setChaveParaRevogar] =
    useState<ChaveAplicacao | null>(null);

  const carregarEmpresas = useCallback(async () => {
    try {
      setError('');
      const lista = api.utils.trataResponseArray(
        await api.get(API_ROUTERS.EMPRESA)
      ) as Empresa[];
      setEmpresas(lista);
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
      setError(
        err instanceof Error ? err.message : 'Erro ao carregar as empresas'
      );
    }
  }, []);

  useEffect(() => {
    carregarEmpresas();
  }, [carregarEmpresas]);

  const handleOpenModal = (empresa?: Empresa) => {
    if (empresa) {
      setFormData({
        codigo: empresa.codigo,
        identificador: empresa.identificador,
        nome: empresa.nome || '',
        banco_erp: empresa.banco_erp || '',
        url_erp: empresa.url_erp || '',
        // vem mascarado; devolvido como está, o servidor preserva o token
        token_erp: empresa.token_erp || '',
        ativo: empresa.ativo,
      });
    } else {
      setFormData(initialFormData);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData(initialFormData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((anterior) => ({
      ...anterior,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!formData.identificador.trim()) {
        throw new Error('Informe o identificador da empresa');
      }

      Store.load.show(dispatch, 'Gravando empresa');

      if (formData.codigo) {
        await api.put(`${API_ROUTERS.EMPRESA}/${formData.codigo}`, formData);
      } else {
        await api.post(API_ROUTERS.EMPRESA, formData);
      }

      Store.dialog.alert(dispatch, 'Empresa gravada com sucesso!', 'success');
      handleCloseModal();
      carregarEmpresas();
    } catch (err) {
      console.error('Erro ao gravar empresa:', err);
      Store.dialog.alert(
        dispatch,
        err instanceof Error ? err.message : 'Erro ao gravar a empresa',
        'error'
      );
    } finally {
      Store.load.hide(dispatch);
    }
  };

  const carregarChaves = async (empresa: Empresa) => {
    const resposta = await api.get(
      `${API_ROUTERS.EMPRESA}/${empresa.codigo}/chave`
    );
    setChaves((resposta?.chaves || []) as ChaveAplicacao[]);
  };

  const handleOpenChaves = async (empresa: Empresa) => {
    setEmpresaSelecionada(empresa);
    setChaveGerada(null);
    setChaves([]);
    setIsChavesModalOpen(true);

    try {
      Store.load.show(dispatch, 'Carregando chaves');
      await carregarChaves(empresa);
    } catch (err) {
      console.error('Erro ao carregar chaves:', err);
      Store.dialog.alert(
        dispatch,
        err instanceof Error ? err.message : 'Erro ao carregar as chaves',
        'error'
      );
    } finally {
      Store.load.hide(dispatch);
    }
  };

  const handleCloseChaves = () => {
    setIsChavesModalOpen(false);
    setEmpresaSelecionada(null);
    setChaves([]);
    setChaveGerada(null);
  };

  const handleGerarChave = async () => {
    if (!empresaSelecionada) return;

    try {
      Store.load.show(dispatch, 'Gerando chave');
      const resposta = await api.post(
        `${API_ROUTERS.EMPRESA}/${empresaSelecionada.codigo}/chave`,
        { propagar }
      );

      setChaveGerada({
        tokenaplicacao: resposta?.chave?.tokenaplicacao || '',
        propagada: Boolean(resposta?.propagacao?.ok),
        erroPropagacao: resposta?.propagacao?.erro || null,
      });

      await carregarChaves(empresaSelecionada);
      carregarEmpresas();
    } catch (err) {
      console.error('Erro ao gerar chave:', err);
      Store.dialog.alert(
        dispatch,
        err instanceof Error ? err.message : 'Erro ao gerar a chave',
        'error'
      );
    } finally {
      Store.load.hide(dispatch);
    }
  };

  const handleRevogarChave = (chave: ChaveAplicacao) => {
    setChaveParaRevogar(chave);
  };

  /**
   * Grava no ERP uma chave que ja existe. Serve para quando o ERP e o Entregas
   * saem de sincronia -- sem isso, a unica forma de acertar seria gerar uma
   * chave nova, rotacionando sem necessidade.
   */
  const handlePropagarChave = async (chave: ChaveAplicacao) => {
    if (!empresaSelecionada) return;

    try {
      Store.load.show(dispatch, 'Gravando a chave no ERP');
      const resposta = await api.post(
        `${API_ROUTERS.EMPRESA}/${empresaSelecionada.codigo}/propagar-chave`,
        { codigoaplicacao: chave.codigoaplicacao }
      );

      const anterior = resposta?.anterior
        ? ` A chave anterior no ERP era ${resposta.anterior}.`
        : '';

      Store.dialog.alert(
        dispatch,
        `Chave ${chave.nomeaplicacao} gravada no ERP.${anterior}`,
        'success'
      );
    } catch (err) {
      console.error('Erro ao gravar a chave no ERP:', err);
      Store.dialog.alert(
        dispatch,
        err instanceof Error ? err.message : 'Erro ao gravar a chave no ERP',
        'error'
      );
    } finally {
      Store.load.hide(dispatch);
    }
  };

  const confirmarRevogacao = async () => {
    if (!chaveParaRevogar || !empresaSelecionada) return;

    try {
      Store.load.show(dispatch, 'Revogando chave');
      await api.delete(
        `${API_ROUTERS.EMPRESA}/${empresaSelecionada.codigo}/chave?codigoaplicacao=${chaveParaRevogar.codigoaplicacao}`
      );
      Store.dialog.alert(dispatch, 'Chave revogada com sucesso!', 'success');
      await carregarChaves(empresaSelecionada);
    } catch (err) {
      console.error('Erro ao revogar chave:', err);
      Store.dialog.alert(
        dispatch,
        err instanceof Error ? err.message : 'Erro ao revogar a chave',
        'error'
      );
    } finally {
      Store.load.hide(dispatch);
      setChaveParaRevogar(null);
    }
  };

  useEffect(() => {
    if (chaveParaRevogar) {
      Store.dialog.confirm(
        dispatch,
        `Revogar a chave "${chaveParaRevogar.nomeaplicacao}"? O ERP que ainda estiver usando ela deixa de conseguir consultar o Entregas.`,
        confirmarRevogacao,
        () => setChaveParaRevogar(null)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveParaRevogar]);

  return (
    <CadastrosEmpresa
      empresas={empresas}
      error={error}
      formData={formData}
      isModalOpen={isModalOpen}
      isChavesModalOpen={isChavesModalOpen}
      empresaSelecionada={empresaSelecionada}
      chaves={chaves}
      chaveGerada={chaveGerada}
      propagar={propagar}
      setPropagar={setPropagar}
      handleOpenModal={handleOpenModal}
      handleCloseModal={handleCloseModal}
      handleOpenChaves={handleOpenChaves}
      handleCloseChaves={handleCloseChaves}
      handleSubmit={handleSubmit}
      handleChange={handleChange}
      handleGerarChave={handleGerarChave}
      handleRevogarChave={handleRevogarChave}
      handlePropagarChave={handlePropagarChave}
    />
  );
};

export default CadastrosEmpresaContainer;
